import { IColumnData } from "@Utils";
import { Card, Col, Row, Typography } from "antd";
import React, { PropsWithChildren, useState } from "react";
import DynamicTable from "./DynamicTable";
import { IKeyedArray } from "./types";

type IOnReady = (plot: unknown) => unknown;

interface IProps<T> {
    chart?: any; // eslint-disable-line @typescript-eslint/no-explicit-any
    data: { chartData: unknown[]; tableData: IKeyedArray | unknown[] };
    columnData: IColumnData<T>[];
    isLoading?: boolean;
    onReady?: IOnReady;
    rowKey?: string;
    [key: string]: unknown; // can receive any prop that ant-design charts receive
}

export interface GroupedChartData {
    group?: string;
    groupValue?: number;
    groups?: string[];
    item?: string;
    name: string;
    value: number;
}

function ChartWithTable<T extends object>({
    chart: Chart,
    data,
    columnData,
    isLoading = false,
    onReady: onReadyProp,
    rowKey,
    ...otherProps
}: PropsWithChildren<IProps<T>>) {
    const [datum, setDatum] = useState<GroupedChartData>();
    const [isGrouped, setIsGrouped] = useState(data?.chartData?.length > 5);

    const onReady = (plot) => {
        document.addEventListener("chart:is-grouped-toggle", (event: CustomEvent<{ isGrouped: boolean }>) => {
            setDatum(undefined);
            setIsGrouped(event.detail.isGrouped);
        });

        plot.on("element:click", ({ data: elementData }: { data: { data: GroupedChartData } }) => {
            setDatum((stateDatum) => {
                if (!elementData?.data?.group) return null;
                return stateDatum?.item !== elementData.data.item ? elementData.data : null;
            });
        });

        onReadyProp?.(plot); // call original onReady if present
    };

    const dataSource = isGrouped
        ? data?.tableData?.[datum?.group]
        : data?.tableData?.[datum?.group]?.filter((d) => d.item === datum?.item);

    const title = isGrouped ? datum?.groups?.join(" - ") ?? "" : datum?.name;

    return (
        <Row>
            <Col md={!!datum?.name ? 8 : 24} xs={24}>
                <Chart {...otherProps} active={datum?.name} data={data.chartData} onReady={onReady} />
            </Col>
            {!!datum?.name ? (
                <Col md={16} xs={24}>
                    <Card className="newUI-subtle">
                        <Card.Grid className="full-width" hoverable={false}>
                            <Typography.Title level={4}>{title}</Typography.Title>
                            <DynamicTable
                                dataSource={dataSource ?? []}
                                columnData={columnData}
                                isLoading={isLoading}
                                pagination={{ pageSize: 5, position: ["bottomCenter"] }}
                                rowKey={rowKey}
                            />
                        </Card.Grid>
                    </Card>
                </Col>
            ) : null}
        </Row>
    );
}

export default ChartWithTable;
